<template>
  <ValidationProvider :rules="rules" :vid="prefixLabel + ' ' + label" immediate v-slot="{ failed, errors }">
    <md-field ref="md-field" :class="{ 'md-error': failed, 'md-form-group': icon, disabled, outline, dynamic }" :md-clearable="clearable">
      <md-icon v-if="icon">{{ icon }}</md-icon>
      <label>
        {{ label }}
        <span v-if="required">&nbsp;*</span>
      </label>

      <span v-if="prefix" class="md-prefix">{{ prefix }}</span>
      <md-input v-model="string_value" @keyup.enter="$emit('done', $event)" @keydown="handleDateInput" type="text" autocomplete="url" :disabled="disabled" :readonly="readonly"> </md-input>
      <span v-if="suffix" class="md-suffix"><small>{{ suffix }}</small></span>

      <slide-y-down-transition><md-icon class="error" v-show="failed">close</md-icon></slide-y-down-transition>
    </md-field>
    <span v-if="conversion_error" class="error-msg">{{ conversion_error }}</span>
    <span v-else-if="failed" class="error-msg">{{ errormsg }}</span>
    <span v-else-if="format_hint" class="error-msg">{{ format_hint }}</span>
  </ValidationProvider>
</template>

<script>
import { SlideYDownTransition } from "vue2-transitions";
import {
  ValueValidationError,
  IntermediateValueError,
  AutocompleteRequest,
  types
} from './types';

export default {
  components: {
    SlideYDownTransition,
  },
  data() {
    return {
      md_field: null,
      input_element: null,
      string_value: '',
      conversion_error: null,
      format_hint: null,
    };
  },
  props: {
    value: {
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: ""
    },
    label: {
      type: String,
      default: ""
    },
    prefix: {
      type: String,
      default: ""
    },
    suffix: {
      type: String,
      default: ""
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    alpha: {
      type: Boolean,
      default: false
    },
    alphaDash: {
      type: Boolean,
      default: false
    },
    alphaNum: {
      type: Boolean,
      default: false
    },
    alphaSpaces: {
      type: Boolean,
      default: false
    },
    regex: {
      type: String,
      default: ""
    },
    min: {
      type: [Number, String],
      default: null,
    },
    max: {
      type: [Number, String],
      default: null,
    },
    errormsg: {
      type: String,
      default: "Please enter the correct information.",
    },
    names: {
      type: String,
      default: "",
    },
    outline: {
      type: Boolean,
      default: false,
    },
    dynamic: {
      type: Boolean,
      default: false,
    },
    type: {
      type: [Object, String, null],
      default: null,
    },
    prefixLabel: {
      type: String,
      default: "",
    },
    prefixYear: {
      type: String,
      default: "",
    },
    minValue: {
      type: [Number],
      default: null,
    }
  },
  watch: {
    // any value coming in through v-model is converted to string via the types string method
    value: {
      handler(value) {
        this.string_value = this.type_instance.string(value);
      },
      immediate: true,
    },

    // any time the input is changed, the string input value is converted to the types value
    string_value(value, previous_value) {
      this.setValue(value, previous_value, true);
    },
  },
  computed: {
    // instantiates a type
    type_instance() {
      if (this.type == null) {
        // default
        return new types.string();

      } else if (typeof this.type == 'string') {
        // attempt to find class in types from string
        let _class = this.type;
        if (_class in types) {
          let type = types[_class];
          if (typeof type == 'function') {
            // found type is type class
            return new type();

          } else {
            // found type is type instance
            return type;
          }

        } else {
          // no type found, return default
          return new types.string();
        }

      } else if (typeof this.type == 'function') {
        // assume function is class constructor
        return new this.type();

      } else if (typeof this.type == 'object') {
        // assume type is instantiated type object
        return this.type;

      } else {
        // default
        return new types.string();
      }
    },

    // DEPRECATED!
    // defines vee-validate compatible array of rules, replaced by the types build in validation
    rules(){
      let rules = [];
      if (this.type === 'CURRENCY' && this.minValue !== null) {
        const cleaned = Number(String(this.string_value).replace(/,/g, ''));
        if (!isNaN(cleaned) && cleaned === 0) {
          rules.push(`min_value:${this.minValue}`);
        }
      }
      if(this.alpha) rules.push('alpha');
      if(this.alphaNum) rules.push(`alpha_num:${this.alphaNum}`);
      if(this.alphaDash) rules.push(`alpha_dash:${this.alphaDash}`);
      if(this.alphaSpaces) rules.push(`alpha_spaces:${this.alphaSpaces}`);
      if(this.min !== null) rules.push(`min:${this.min}`);
      if(this.max !== null) rules.push(`max:${this.max}`);
      if(this.required) rules.push('required');
      if(this.regex) rules.push(`regex:${this.regex}`);
      if(this.names) rules.push('names');
      if(this.url) rules.push('url');

      if(this.type_instance.validators) {
        rules = rules.concat(this.type_instance.validators());
      }

      return rules.join('|');
    },
  },
  methods: {
    handleDateInput(e) {
      this.$emit('keydown', e)
      if (this.type == 'REQUIRED_DATE' || this.type == 'DATE') {
        // allow numeric, ArrowLeft(37), ArrowRight(39), Backspace(8), Enter(13), Tab(9)
        if (isNaN(e.key) && e.keyCode !== 37 && e.keyCode !== 39 && e.keyCode !== 8 && e.keyCode !== 13 && e.keyCode !== 9) {
          e.preventDefault();
        }
      }
    },
    // converts the given string value to the types value and sets the external v-model value
    setValue(value, previous_value, live) {

      // try to convert string to value
      try {
        let typed_value = this.type_instance.value(value, live);
        this.conversion_error = null;
        this.format_hint = null;
        this.$emit('input', typed_value);

        return true;

      // it the conversion fails, the type may communicate actions and errors back
      } catch (error) {

        // the string failed validation, display the validation message
        if(error instanceof ValueValidationError){
          this.format_hint = null;
          this.conversion_error = error.message;

        // the string is imcomplete and could not yet be converted
        } else if (error instanceof IntermediateValueError) {
          this.format_hint = error.message;
          this.conversion_error = null;

        // the type is requesting a string is appended to the current string value
        } else if (error instanceof AutocompleteRequest) {
          // only apply auto complete action if going forwards and cursor is at the end
          let cursor_position = this.input_element.selectionStart;
          if (value.length > previous_value.length && value.length == cursor_position) {
            this.string_value = `${this.string_value}${error.message}`;
          }

        // an unknown error occured
        } else {
          console.error('failed applying type to value, externam v-model has not been updated', error.constructor.name, error);
        }
      }

      return false;
    },
    onInputFocus(event) {
    },
    // when leaving the input field, attempt to set the value, and then set the internal string value to the formatted string
    // note if the string value does not pass validation, the external v-model value does not get updated and the internal
    // string value is reset to the formatted string of the last valid value
    onInputBlur(event) {
      if (this.type == 'REQUIRED_DATE' || this.type == 'DATE') {
        let values = this.string_value.split("/");
        // have input year and year only 2 digital
        if (values.length == 3 && values[2].length == 2) {
          if (this.prefixYear == "future") {
            values[2] = `20${values[2]}`;
          } else if (this.prefixYear == "past") {
            let year = `19${values[2]}`;
            let currentYear = (new Date()).getFullYear();
            values[2] = ((currentYear-parseInt(year)) > 100) ? `20${values[2]}` : year;
          }
        }
        // set value
        this.setValue(values.join("/"), this.string_value, false);
      } else {
        this.setValue(this.string_value, this.string_value, false);
      }
    },
    oninputKeyUp(event) {
    },
  },

  // register any native event listeners we need
  mounted() {
    // add blur and keyup event
    this.md_field = this.$refs["md-field"];
    this.input_element = this.md_field.$el.querySelector("input");

    this.input_element.addEventListener("focus", this.onInputFocus);
    this.input_element.addEventListener("blur", this.onInputBlur);
    this.input_element.addEventListener("keyup", this.oninputKeyUp);
  },

  // remove any native event listeners we needed
  beforeUnmount() {
    // remove blur and keyup event
    let md_field = this.md_field || this.$refs["md-field"];
    let input_element = this.input_element || md_field.$el.querySelector("input");
    input_element.removeEventListener("focus", this.onInputFocus);
    input_element.removeEventListener("blur", this.onInputBlur);
    input_element.removeEventListener("keyup", this.oninputKeyUp);
    this.input_element = null;
    this.md_field = null;
  },
};
</script>

<style lang="scss" scoped>
  .disabled{
    opacity: 0.7;
  }
</style>
